import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject, map, of, takeUntil } from 'rxjs';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { AcaoEtapaEnum } from '@shared/enums/compras/acao-etapa.enum';
import { StatusConferenciaEnum } from '@shared/enums/compras/status-conferencia.enum';
import { StatusItemConferenciaEnum } from '@shared/enums/compras/status-item-conferencia.enum';
import { TipoDivergenciaCompraEnum } from '@shared/enums/compras/tipo-divergencia-compra.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ConferenciaService } from '@shared/services/apis/compras/conferencia.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { ConferenciaSituacaoResponse } from '@shared/services/responses/compras/conferencia-situacao.response';
import { NotaFiscalEntradaContextoVinculoPedidoResponse } from '@shared/services/responses/orquestrador-compras/nota-fiscal-entrada-contexto-vinculo-pedido.response';
import { ConferenciaStruct } from '@shared/services/structs/compras/conferencia.struct';
import { VinculoPedidoNotaStruct } from '@shared/services/structs/orquestrador-compras/vinculo-pedido-nota.struct';
import { KanbanCard } from '../../../processo-compras.models';
import { ConferenciaDialogComponent } from '../../conferencia-dialog/conferencia-dialog.component';
import { ConferenciaDialogData, ConferenciaDialogResult } from '../../conferencia-dialog/conferencia-dialog.data';

/**
 * Painel da conferência física na etapa Conferência (abaixo do bloco da NF): "Receber mercadorias"
 * (volumes) e "Conferir itens", independentes e em qualquer ordem. A conferência é da NF do modo NF do card,
 * a mesma para todos os pedidos dela. Cada ação abre a tela cheia de contagem; ao voltar, a situação é
 * recarregada e `alterado` avisa o card para o quadro recarregar.
 */
@Component({
  selector: 'app-ced-stage-conferencia', standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, AlertCardComponent],
  templateUrl: './ced-stage-conferencia.component.html', styleUrl: './ced-stage-conferencia.component.css',
})
export class CedStageConferenciaComponent implements OnInit, OnDestroy {
  @Input({ required: true }) card!: KanbanCard;
  /** NF do modo NF do card (`notaModoNf.idNFeEntrada`): chave da conferência. */
  @Input() idNFeEntrada: number | null = null;
  @Input() somenteLeitura = false;
  /**
   * Contexto da NF vindo do bloco da NF da etapa, para não buscar de novo (volumes esperados, EANs e pedidos).
   * `undefined` = aguardando o bloco; `null` = sem o bloco ou falha: busca sozinho. Reemitido após mudança de
   * vínculo. Vem depois de `idNFeEntrada` no template.
   */
  @Input() set contexto(valor: NotaFiscalEntradaContextoVinculoPedidoResponse | null | undefined) {
    if (valor === undefined || !this.idNFeEntrada) return;
    this.carregarNotas(valor
      ? of(valor)
      : this.notasService.contextosVinculoPedido([this.idNFeEntrada]).pipe(map(r => r.isError ? null : r.lista?.[0] ?? null)));
  }
  @Output() alterado = new EventEmitter<void>();
  /**
   * A cada carga da situação: `true` quando há conferência cega (volumes ou itens) ainda não concluída — ação
   * não iniciada conta como não concluída —, para o card ocultar as quantidades. Falha técnica (HTTP) emite `false`;
   * situação recusada pela API (regra de negócio) não emite, mantendo o valor anterior (o card começa em `true`).
   */
  @Output() cegaPendente = new EventEmitter<boolean>();
  /**
   * A contagem concluída agora fechou as duas ações da NF (o mesmo `isConferenciaConcluida` do concluir), com o
   * `idNFeEntrada`: o card consulta a automação "Mover automaticamente" para Finalizado (D-13).
   */
  @Output() conferenciaConcluida = new EventEmitter<number>();

  readonly acao = AcaoEtapaEnum;
  /** Ordem dos cartões: recebimento (volumes) e conferência de itens. */
  readonly acoes = [AcaoEtapaEnum.ConferirVolumes, AcaoEtapaEnum.ConferirItens];
  readonly statusConferencia = StatusConferenciaEnum;
  situacao: ConferenciaSituacaoResponse | null = null;
  /** `errorDescription` da situação recusada pela API (regra de negócio): exibido no bloco como motivo do bloqueio. */
  motivoSituacao = '';
  /** Cabeçalho da NF: faixa da tela cheia e volumes esperados. */
  nota: VinculoPedidoNotaStruct | null = null;
  /** Códigos dos pedidos vinculados à NF, para o cabeçalho da tela cheia. */
  codigosPedido: string[] = [];
  /** EAN de cada item da NF, para a bipagem na tela cheia. */
  eanPorNFeEntradaItem: Record<number, string> = {};
  carregandoSituacao = false;
  /** Começa `true`: até o contexto da NF chegar (do bloco ou da busca própria), "Receber mercadorias" não inicia. */
  carregandoNotas = true;
  erroNotas = '';
  /** Ação sendo iniciada (spinner no cartão). */
  abrindo: AcaoEtapaEnum | null = null;
  /** Ação sendo reaberta (spinner no botão "Reabrir contagem"). */
  reabrindo: AcaoEtapaEnum | null = null;
  private readonly destroy$ = new Subject<void>();
  /** Cancela a carga anterior do contexto da NF quando outro chega. */
  private readonly recargaNotas$ = new Subject<void>();

  constructor(
    private conferenciaService: ConferenciaService,
    private notasService: NotaFiscalEntradaService,
    private alertService: AlertService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.carregarSituacao();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** `quantidadeVolume` da NF; nula quando a nota não informa volumes. */
  get quantidadeVolumesEsperada(): number | null {
    return this.nota?.quantidadeVolume ?? null;
  }

  conferenciaDa(acao: AcaoEtapaEnum): ConferenciaStruct | null {
    if (!this.situacao) return null;
    return acao === AcaoEtapaEnum.ConferirVolumes ? this.situacao.volumes : this.situacao.itens;
  }

  isCega(acao: AcaoEtapaEnum): boolean {
    return acao === AcaoEtapaEnum.ConferirVolumes ? !!this.situacao?.isVolumesCega : !!this.situacao?.isItensCega;
  }

  rotuloStatus(acao: AcaoEtapaEnum): string {
    const conferencia = this.conferenciaDa(acao);
    if (!conferencia) return 'Pendente';
    if (conferencia.idStatusConferencia === StatusConferenciaEnum.Concluida) {
      return acao === AcaoEtapaEnum.ConferirVolumes ? 'Recebido' : 'Conferido';
    }
    return 'Em andamento';
  }

  classeStatus(acao: AcaoEtapaEnum): string {
    const conferencia = this.conferenciaDa(acao);
    if (!conferencia) return 'badge-cinza';
    return conferencia.idStatusConferencia === StatusConferenciaEnum.Concluida ? 'badge-verde' : 'badge-amarelo';
  }

  /**
   * Divergências da ação para o selo do cartão: problemas registrados nela e, depois de concluída, o que a contagem
   * apontou — itens com status Divergente ou volumes recebidos diferentes dos esperados.
   */
  quantidadeDivergencias(acao: AcaoEtapaEnum): number {
    const problemas = (this.situacao?.problemas ?? []).filter(p => p.idAcaoEtapa === acao).length;
    const conferencia = this.conferenciaDa(acao);
    if (conferencia?.idStatusConferencia !== StatusConferenciaEnum.Concluida) return problemas;
    if (acao === AcaoEtapaEnum.ConferirItens) {
      return problemas + conferencia.itens.filter(i => i.idStatusItemConferencia === StatusItemConferenciaEnum.Divergente).length;
    }
    const esperada = conferencia.quantidadeVolumesEsperada;
    const recebida = conferencia.quantidadeVolumesRecebida;
    return problemas + (esperada != null && recebida != null && recebida !== esperada ? 1 : 0);
  }

  /** Motivo do bloqueio do cartão (tooltip); vazio = clicável. Ação concluída fica clicável para visualizar. */
  motivoBloqueio(acao: AcaoEtapaEnum): string {
    if (this.somenteLeitura) return 'Pedido arquivado ou em etapa final.';
    if (!this.idNFeEntrada) return 'Pedido sem nota fiscal vinculada.';
    const situacao = this.situacao;
    if (!situacao) return this.carregandoSituacao ? '' : this.motivoSituacao || 'Não foi possível carregar a situação da conferência.';
    const conferencia = this.conferenciaDa(acao);
    if (conferencia?.idStatusConferencia === StatusConferenciaEnum.Concluida) return '';
    if (situacao.isBloqueada) return situacao.motivoBloqueio || 'Conferência bloqueada.';
    const pode = acao === AcaoEtapaEnum.ConferirVolumes ? situacao.podeConferirVolumes : situacao.podeConferirItens;
    if (!pode) return 'Seu perfil não tem permissão para esta ação nesta etapa.';
    if (acao === AcaoEtapaEnum.ConferirVolumes && !conferencia && this.erroNotas) return this.erroNotas;
    return '';
  }

  isDesabilitado(acao: AcaoEtapaEnum): boolean {
    if (!this.situacao || this.carregandoSituacao || this.abrindo !== null || this.reabrindo !== null) return true;
    // Volumes esperados vêm das notas: sem elas carregadas, não inicia a ação.
    if (acao === AcaoEtapaEnum.ConferirVolumes && !this.conferenciaDa(acao) && this.carregandoNotas) return true;
    return !!this.motivoBloqueio(acao);
  }

  rotuloTipoProblema(idTipoDivergenciaCompra: number): string {
    return idTipoDivergenciaCompra === TipoDivergenciaCompraEnum.Avaria ? 'Volume com avaria' : 'Divergência nas quantidades';
  }

  /** Item do problema (a lista vem na conferência de itens da situação). */
  nomeItemProblema(idConferenciaItem: number | null): string {
    if (idConferenciaItem == null) return '';
    return this.situacao?.itens?.itens.find(i => i.idConferenciaItem === idConferenciaItem)?.nome ?? '';
  }

  abrir(acao: AcaoEtapaEnum): void {
    if (this.isDesabilitado(acao) || !this.idNFeEntrada) return;
    const existente = this.conferenciaDa(acao);
    if (existente) {
      this.abrirTelaCheia(existente, false);
      return;
    }

    this.abrindo = acao;
    this.conferenciaService.iniciar({
      idNFeEntrada: this.idNFeEntrada,
      idAcaoEtapa: acao,
      quantidadeVolumesEsperada: acao === AcaoEtapaEnum.ConferirVolumes ? this.quantidadeVolumesEsperada : null,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: resposta => {
        this.abrindo = null;
        if (resposta.isError || !resposta.conferencia) {
          this.alertService.show('Conferência', resposta.errorDescription || 'Não foi possível iniciar a conferência.', AlertType.error);
          return;
        }
        // Iniciar já muda o progresso do card no quadro.
        this.abrirTelaCheia(resposta.conferencia, true);
      },
      error: (e: unknown) => {
        this.abrindo = null;
        this.alertService.show('Conferência', e instanceof Error ? e.message : 'Não foi possível iniciar a conferência.', AlertType.error);
      },
    });
  }

  /** "Reabrir contagem": ação concluída, permissão "Alterar situação" da etapa e card editável. */
  podeReabrir(acao: AcaoEtapaEnum): boolean {
    return !this.somenteLeitura
      && !!this.situacao?.podeAlterarSituacaoManualmente
      && this.conferenciaDa(acao)?.idStatusConferencia === StatusConferenciaEnum.Concluida;
  }

  /** Pede a justificativa e volta a ação para Em andamento (no modo cego, a contagem recomeça do zero). */
  reabrir(acao: AcaoEtapaEnum): void {
    const conferencia = this.conferenciaDa(acao);
    if (!conferencia || !this.podeReabrir(acao) || this.reabrindo !== null) return;
    const nomeAcao = acao === AcaoEtapaEnum.ConferirVolumes ? 'o recebimento de mercadorias' : 'a conferência de itens';
    const efeito = this.isCega(acao)
      ? 'Como a conferência é <b>cega</b>, a contagem recomeça do zero: as quantidades informadas são descartadas.'
      : 'As quantidades já informadas são <b>mantidas</b> e podem ser corrigidas antes de concluir de novo.';
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Reabrir contagem',
        innerHtml: `Esta ação reabre ${nomeAcao}, que volta para <b>Em andamento</b>. ${efeito} `
          + 'Os problemas registrados continuam e a justificativa é registrada no histórico dos pedidos da nota.',
        buttonConfirm: 'Reabrir contagem',
        buttonClose: 'Voltar',
        showTextarea: true,
        textareaLabel: 'Justificativa',
        textareaRequired: true,
        textareaMinLength: 3,
      },
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(resultado => {
      if (!resultado?.confirm) return;
      this.reabrindo = acao;
      this.conferenciaService.reabrir({
        idConferencia: conferencia.idConferencia,
        justificativa: (resultado.textareaValue as string).trim(),
      }).pipe(takeUntil(this.destroy$)).subscribe({
        next: resposta => {
          this.reabrindo = null;
          if (resposta.isError) {
            this.alertService.show('Conferência', resposta.errorDescription || 'Não foi possível reabrir a contagem.', AlertType.error);
            return;
          }
          this.alertService.show('Conferência', 'Contagem reaberta.', AlertType.success);
          this.carregarSituacao();
          this.alterado.emit();
        },
        error: (e: unknown) => {
          this.reabrindo = null;
          this.alertService.show('Conferência', e instanceof Error ? e.message : 'Não foi possível reabrir a contagem.', AlertType.error);
        },
      });
    });
  }

  private abrirTelaCheia(conferencia: ConferenciaStruct, iniciada: boolean): void {
    const acao = conferencia.idAcaoEtapa === AcaoEtapaEnum.ConferirVolumes ? AcaoEtapaEnum.ConferirVolumes : AcaoEtapaEnum.ConferirItens;
    const nota = this.nota;
    const data: ConferenciaDialogData = {
      conferencia,
      isCega: this.isCega(acao),
      somenteLeitura: conferencia.idStatusConferencia === StatusConferenciaEnum.Concluida,
      podeRegistrarProblema: !!this.situacao?.podeRegistrarProblema,
      codigosPedido: this.codigosPedido.length ? this.codigosPedido : [this.card.id],
      nota: nota ? { numero: nota.numero, serie: nota.serie, dataEmissao: nota.dataEmissao, nomeEmitente: nota.nomeEmitente } : null,
      eanPorNFeEntradaItem: this.eanPorNFeEntradaItem,
    };
    this.dialog.open<ConferenciaDialogComponent, ConferenciaDialogData, ConferenciaDialogResult>(ConferenciaDialogComponent, {
      width: '96vw', height: '92vh', maxWidth: '100vw', maxHeight: '100vh',
      disableClose: true, autoFocus: false, panelClass: 'conferencia-dialog', data,
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(resultado => {
      // Situação de antes da recarga: a outra ação já concluída + esta concluída agora = conferência da NF concluída.
      const outra = acao === AcaoEtapaEnum.ConferirVolumes ? this.situacao?.itens : this.situacao?.volumes;
      const isConferenciaConcluida = !!resultado?.concluido && outra?.idStatusConferencia === StatusConferenciaEnum.Concluida;
      this.carregarSituacao();
      if (iniciada || resultado?.alterado) this.alterado.emit();
      if (isConferenciaConcluida && this.idNFeEntrada) this.conferenciaConcluida.emit(this.idNFeEntrada);
    });
  }

  private carregarSituacao(): void {
    // Sem NF não há conferência: libera as quantidades do card.
    if (!this.idNFeEntrada) { this.cegaPendente.emit(false); return; }
    this.carregandoSituacao = true;
    this.conferenciaService.obterSituacao(this.idNFeEntrada).pipe(takeUntil(this.destroy$)).subscribe({
      next: resposta => {
        this.carregandoSituacao = false;
        if (resposta.isError) {
          // Regra de negócio (pedidos da NF fora da etapa, NF sem pedido ativo): motivo no bloco, sem toast. Sem
          // saber o modo, as quantidades continuam ocultas (cegaPendente não é emitido).
          this.situacao = null;
          this.motivoSituacao = resposta.errorDescription || 'Não foi possível carregar a conferência.';
          return;
        }
        this.motivoSituacao = '';
        this.situacao = resposta;
        const concluida = (c: ConferenciaStruct | null | undefined) => c?.idStatusConferencia === StatusConferenciaEnum.Concluida;
        this.cegaPendente.emit((resposta.isVolumesCega && !concluida(resposta.volumes)) || (resposta.isItensCega && !concluida(resposta.itens)));
      },
      error: (e: unknown) => {
        this.carregandoSituacao = false;
        this.cegaPendente.emit(false);
        this.alertService.show('Conferência', e instanceof Error ? e.message : 'Não foi possível carregar a conferência.', AlertType.error);
      },
    });
  }

  private carregarNotas(contexto$: Observable<NotaFiscalEntradaContextoVinculoPedidoResponse | null>): void {
    this.recargaNotas$.next();
    this.carregandoNotas = true;
    this.erroNotas = '';
    contexto$.pipe(takeUntil(this.recargaNotas$), takeUntil(this.destroy$)).subscribe({
      next: contexto => {
        this.carregandoNotas = false;
        if (!contexto || contexto.isError) {
          this.erroNotas = 'Não foi possível carregar a nota vinculada para obter os volumes esperados.';
          return;
        }
        this.nota = contexto.nota;
        this.codigosPedido = [...new Set((contexto.vinculosPedido ?? []).map(v => v.codigoCompra).filter(c => !!c))];
        // EAN do XML por item da NF; "SEM GTIN" e vazios ficam de fora.
        const eans: Record<number, string> = {};
        for (const item of contexto.itens ?? []) {
          const ean = (item.codigoEan ?? '').trim();
          if (/^\d{8,14}$/.test(ean)) eans[item.idNFeEntradaItem] = ean;
        }
        this.eanPorNFeEntradaItem = eans;
      },
      error: () => {
        this.carregandoNotas = false;
        this.erroNotas = 'Não foi possível carregar a nota vinculada para obter os volumes esperados.';
      },
    });
  }
}
